import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, User, ShoppingBag, Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { CartDrawer } from "./CartDrawer";

const LINKS = ["SHOP", "COLLECTIONS", "ABOUT", "ATHLETES", "JOURNAL"];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (section) => {
    const el = document.getElementById(section);
    if (el) {
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
      else el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const go = (section) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: section } });
    } else {
      scrollTo(section);
    }
  };

  const goHome = () => {
    if (location.pathname !== "/") navigate("/");
    else if (window.__lenis) window.__lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openCart = () => setCartOpen(true);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1600px] px-6 md:px-10 h-20 flex items-center justify-between">
        {/* Logo */}
        <button
          data-testid="logo-home"
          onClick={goHome}
          className="flex items-center gap-3 group"
        >
          <img
            src="/generated/wolf_white.png"
            alt="Lone Wolf"
            className="w-9 h-9 object-contain transition-transform duration-500 group-hover:-translate-y-0.5"
          />
          <span className="flex flex-col leading-none text-left">
            <span className="font-display font-extrabold tracking-[0.18em] text-lg text-white">LONE WOLF</span>
            <span className="font-sans tracking-[0.42em] text-[9px] text-white/50">GYM GEAR</span>
          </span>
        </button>

        {/* Desktop links */}
        <ul className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => (
            <li key={l}>
              <button
                data-testid={`nav-${l.toLowerCase()}`}
                onClick={() => go(l === "COLLECTIONS" ? "collections" : l === "ABOUT" ? "manifesto" : l === "SHOP" ? "collections" : "footer")}
                className="relative font-sans text-xs tracking-[0.2em] text-white/70 hover:text-white transition-colors duration-300 py-2 group"
              >
                {l}
                <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-white transition-all duration-300 group-hover:w-full" />
              </button>
            </li>
          ))}
        </ul>

        {/* Icons */}
        <div className="flex items-center gap-5">
          <button data-testid="search-btn" className="hidden sm:block text-white/70 hover:text-white transition-colors" aria-label="Search">
            <Search className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <button data-testid="account-btn" className="hidden sm:block text-white/70 hover:text-white transition-colors" aria-label="Account">
            <User className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <button data-testid="cart-btn" onClick={openCart} className="relative text-white/80 hover:text-white transition-colors" aria-label="Cart">
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            <span className="absolute -top-2 -right-2 text-[10px] font-sans text-white/90">{count}</span>
          </button>
          <button data-testid="menu-toggle" onClick={() => setOpen((o) => !o)} className="lg:hidden text-white" aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-b border-white/10"
          >
            <ul className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <li key={l}>
                  <button
                    onClick={() => go(l === "COLLECTIONS" || l === "SHOP" ? "collections" : l === "ABOUT" ? "manifesto" : "footer")}
                    className="font-display text-2xl font-bold uppercase tracking-normal text-white/80 hover:text-white"
                  >
                    {l}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      <CartDrawer />
    </motion.header>
  );
};

export default Navbar;
